import { MIN_YEAR, THRESHOLD } from './content';

export type Mode = 'revenue' | 'profit';
export type ResultLevel = 'none' | 'small' | 'lowRate' | 'relevant';

// Revenue mode: profit ≈ 70 % of revenue (Pauschalierung + Gewinnfreibetrag).
const PROFIT_SHARE = 0.7;
// Below this profit the marginal tax rate is at most 20 % – a prepayment
// saves little tax, so it is not urgent (Pascal, 28.09.).
export const LOW_RATE_PROFIT = 20_000;

export const RATE = { revenue: 0.2, profit: 0.2683 } as const;

// Höchstbeitragsgrundlage 2026 (GSVG): 97.020 € pro Jahr → maximal ca.
// 26.030 € (26,83 %) + Unfallversicherung 155,52 € ≈ 26.200 € SVS pro Jahr.
// Grob gerundet wie der Rest des Rechners.
export const MAX_BASE_YEAR = 97_020;
export const MAX_YEAR = roundTo100(MAX_BASE_YEAR * 0.2683 + 12.95 * 12);

function roundTo100(value: number) {
  return Math.round(value / 100) * 100;
}

export const INCOME_RANGE = {
  revenue: { min: 10_000, max: 150_000, step: 500, initial: 50_000 },
  profit: { min: 6_500, max: 150_000, step: 500, initial: 50_000 },
} as const;

// One shared slider scale for both modes, so 50.000 € Gewinn sits at the same
// thumb position as 50.000 € Umsatz and switching modes never makes the
// slider jump. Revenue simply can't go below its own minimum (clamped).
export const INCOME_SLIDER = { min: 6_500, max: 150_000, step: 500 } as const;
export const clampIncome = (mode: Mode, value: number) =>
  Math.min(INCOME_RANGE[mode].max, Math.max(INCOME_RANGE[mode].min, value));

// "Bisher bezahlt für 2026": 0 bis zum Höchstbeitrag in 500er-Schritten, plus
// Mindestbeitrag (1.929,72 €, Startwert) und Höchstbeitrag als eigene Stopps.
// Mehr als den Höchstbeitrag kann man für ein Jahr nicht schulden.
export const PAID_STOPS: number[] = Array.from(
  new Set([...Array.from({ length: Math.floor(MAX_YEAR / 500) + 1 }, (_, i) => i * 500), MIN_YEAR, MAX_YEAR]),
).sort((a, b) => a - b);
export const PAID_INITIAL_INDEX = PAID_STOPS.indexOf(MIN_YEAR);

export const roundTo = (value: number, step = 100) => Math.round(value / step) * step;

export function estimate(mode: Mode, income: number, paid: number) {
  // Between the minimum contribution (due regardless) and the maximum
  // (Höchstbeitragsgrundlage).
  const raw = income * RATE[mode];
  const atMin = raw <= MIN_YEAR;
  const atMax = raw >= MAX_YEAR;
  const svsYear = Math.min(MAX_YEAR, Math.max(MIN_YEAR, raw));
  const open = svsYear - paid;
  const profit = mode === 'revenue' ? income * PROFIT_SHARE : income;
  const level: ResultLevel =
    open <= 0 ? 'none' : open < THRESHOLD ? 'small' : profit < LOW_RATE_PROFIT ? 'lowRate' : 'relevant';
  return {
    atMin,
    atMax,
    svsYear: atMin ? MIN_YEAR : roundTo(svsYear),
    open: Math.max(0, roundTo(open)),
    level,
  };
}

export const formatEuro = (value: number) =>
  new Intl.NumberFormat('de-AT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
  }).format(value);
